/**
 * FilterRuleDialog - Create/Edit dialog for filter rules
 *
 * Features:
 * - Condition builder with multiple condition types
 * - Action builder with multiple action types
 * - Live test/preview of matching links
 * - Webhook configuration for webhook actions
 */

import { ArrowLeft, Plus, TestTube2, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
	ACTION_LABELS,
	type Action,
	type ActionType,
	type Condition,
	CONDITION_LABELS,
	type ConditionType,
	type CreateFilterRuleInput,
	type FilterRule,
	LINK_SOURCES,
	type WebhookConfig,
} from '@/api/filters';
import type { QueueResponse, TagResponse } from '@/api/client';
import { useQueuesQuery, useTagsQuery } from '@/hooks/useGraphQLQueries';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
	useCreateFilterRule,
	useTestFilterConditions,
	useUpdateFilterRule,
} from '@/hooks/useFilterRules';

interface FilterRuleDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	rule: FilterRule | null; // null = create mode
	onClose: () => void;
}

interface FormData {
	name: string;
	description: string;
	conditions: Condition[];
	actions: Action[];
	stopProcessing: boolean;
}

const DEFAULT_CONDITION: Condition = { type: 'url_domain', value: '' };
const DEFAULT_ACTION: Action = { type: 'add_tag', value: [] };

const CONDITION_TYPES: ConditionType[] = [
	'url_domain',
	'url_path',
	'url_pattern',
	'has_tag',
	'missing_tag',
	'title_contains',
	'description_contains',
	'source_equals',
	'metadata_field',
];

const ACTION_TYPES: ActionType[] = [
	'add_tag',
	'remove_tag',
	'set_queue',
	'archive',
	'delete',
	'webhook',
];

export function FilterRuleDialog({
	open,
	onOpenChange,
	rule,
	onClose,
}: FilterRuleDialogProps) {
	const isEditing = !!rule;

	const [formData, setFormData] = useState<FormData>({
		name: '',
		description: '',
		conditions: [{ ...DEFAULT_CONDITION }],
		actions: [{ ...DEFAULT_ACTION }],
		stopProcessing: false,
	});
	const [error, setError] = useState<string | null>(null);
	const [testResults, setTestResults] = useState<{
		matched: number;
		total: number;
		results: Array<{ linkId: number; url: string; title: string | null; matched: boolean }>;
	} | null>(null);

	// Use GraphQL queries for tags and queues - urql's cache handles deduplication
	const { tags } = useTagsQuery({ includeNsfw: true });
	const { queues } = useQueuesQuery();

	const createMutation = useCreateFilterRule();
	const updateMutation = useUpdateFilterRule();
	const testMutation = useTestFilterConditions();

	const isSubmitting = createMutation.isPending || updateMutation.isPending;

	// Initialize form when rule changes
	useEffect(() => {
		if (rule) {
			setFormData({
				name: rule.name,
				description: rule.description || '',
				conditions: rule.conditions.length > 0 ? [...rule.conditions] : [{ ...DEFAULT_CONDITION }],
				actions: rule.actions.length > 0 ? [...rule.actions] : [{ ...DEFAULT_ACTION }],
				stopProcessing: rule.stopProcessing,
			});
		} else {
			setFormData({
				name: '',
				description: '',
				conditions: [{ ...DEFAULT_CONDITION }],
				actions: [{ ...DEFAULT_ACTION }],
				stopProcessing: false,
			});
		}
		setError(null);
		setTestResults(null);
	}, [rule]);

	// Condition helpers
	function updateCondition(index: number, updates: Partial<Condition>) {
		setFormData((prev) => ({
			...prev,
			conditions: prev.conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)),
		}));
	}

	function addCondition() {
		setFormData((prev) => ({
			...prev,
			conditions: [...prev.conditions, { ...DEFAULT_CONDITION }],
		}));
	}

	function removeCondition(index: number) {
		if (formData.conditions.length <= 1) return;
		setFormData((prev) => ({
			...prev,
			conditions: prev.conditions.filter((_, i) => i !== index),
		}));
	}

	// Action helpers
	function updateAction(index: number, updates: Partial<Action>) {
		setFormData((prev) => ({
			...prev,
			actions: prev.actions.map((a, i) => (i === index ? { ...a, ...updates } : a)),
		}));
	}

	function addAction() {
		setFormData((prev) => ({
			...prev,
			actions: [...prev.actions, { ...DEFAULT_ACTION }],
		}));
	}

	function removeAction(index: number) {
		if (formData.actions.length <= 1) return;
		setFormData((prev) => ({
			...prev,
			actions: prev.actions.filter((_, i) => i !== index),
		}));
	}

	// Handle test
	function handleTest() {
		const validConditions = formData.conditions.filter((c) => c.value.trim() !== '');
		if (validConditions.length === 0) {
			setError('Add at least one condition with a value to test');
			return;
		}

		testMutation.mutate(
			{ conditions: validConditions, options: { limit: 20 } },
			{
				onSuccess: (result) => {
					setTestResults(result);
					setError(null);
				},
				onError: (err) => {
					setError(err instanceof Error ? err.message : 'Test failed');
				},
			}
		);
	}

	// Handle submit
	const handleSubmit = useCallback(async () => {
		// Validate
		if (!formData.name.trim()) {
			setError('Name is required');
			return;
		}

		const validConditions = formData.conditions.filter((c) => c.value.trim() !== '');
		if (validConditions.length === 0) {
			setError('At least one condition is required');
			return;
		}

		const validActions = formData.actions.filter((a) => {
			if (a.type === 'archive' || a.type === 'delete') return true;
			if (a.type === 'webhook') {
				const config = a.value as WebhookConfig;
				return config && config.url && config.url.trim() !== '';
			}
			if (Array.isArray(a.value)) return a.value.length > 0;
			return typeof a.value === 'string' && a.value.trim() !== '';
		});
		if (validActions.length === 0) {
			setError('At least one action is required');
			return;
		}

		setError(null);

		const input: CreateFilterRuleInput = {
			name: formData.name.trim(),
			description: formData.description.trim() || undefined,
			conditions: validConditions,
			actions: validActions,
			stop_processing: formData.stopProcessing,
		};

		try {
			if (isEditing && rule) {
				await updateMutation.mutateAsync({ id: rule.id, data: input });
			} else {
				await createMutation.mutateAsync(input);
			}
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save rule');
		}
	}, [formData, isEditing, rule, createMutation, updateMutation, onClose]);

	// Render condition value input based on type
	function renderConditionValue(condition: Condition, index: number) {
		switch (condition.type) {
			case 'has_tag':
			case 'missing_tag':
				return (
					<Select
						value={condition.value}
						onValueChange={(value) => updateCondition(index, { value })}
					>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Select tag..." />
						</SelectTrigger>
						<SelectContent>
							{tags.map((tag: TagResponse) => (
								<SelectItem key={tag.id} value={tag.name}>
									{tag.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);

			case 'source_equals':
				return (
					<Select
						value={condition.value}
						onValueChange={(value) => updateCondition(index, { value })}
					>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Select source..." />
						</SelectTrigger>
						<SelectContent>
							{LINK_SOURCES.map((source) => (
								<SelectItem key={source.value} value={source.value}>
									{source.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				);

			case 'metadata_field':
				return (
					<div className="flex-1 flex gap-2">
						<Input
							value={condition.field || ''}
							onChange={(e) => updateCondition(index, { field: e.target.value })}
							placeholder="Field name"
							className="w-32"
						/>
						<Input
							value={condition.value}
							onChange={(e) => updateCondition(index, { value: e.target.value })}
							placeholder="Value pattern"
							className="flex-1"
						/>
					</div>
				);

			default:
				return (
					<Input
						value={condition.value}
						onChange={(e) => updateCondition(index, { value: e.target.value })}
						placeholder={getConditionPlaceholder(condition.type)}
						className="flex-1"
					/>
				);
		}
	}

	// Render action value input based on type
	function renderActionValue(action: Action, index: number) {
		switch (action.type) {
			case 'add_tag':
			case 'remove_tag': {
				const currentTags = Array.isArray(action.value) ? action.value : [];
				return (
					<div className="flex-1 space-y-2">
						<Select
							value=""
							onValueChange={(value) => {
								if (!currentTags.includes(value)) {
									updateAction(index, { value: [...currentTags, value] });
								}
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder="Add tag..." />
							</SelectTrigger>
							<SelectContent>
								{tags
									.filter((t: TagResponse) => !currentTags.includes(t.name))
									.map((tag: TagResponse) => (
										<SelectItem key={tag.id} value={tag.name}>
											{tag.name}
										</SelectItem>
									))}
							</SelectContent>
						</Select>
						{currentTags.length > 0 && (
							<div className="flex flex-wrap gap-1">
								{currentTags.map((tag) => (
									<Badge
										key={tag}
										variant="secondary"
										className="cursor-pointer"
										onClick={() =>
											updateAction(index, { value: currentTags.filter((t) => t !== tag) })
										}
									>
										{tag} <X className="size-3 ml-1" />
									</Badge>
								))}
							</div>
						)}
					</div>
				);
			}

			case 'set_queue':
				return (
					<Select
						value={action.value as string}
						onValueChange={(value) => updateAction(index, { value })}
					>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Select queue..." />
						</SelectTrigger>
						<SelectContent>
							{queues
								.filter((q: QueueResponse) => q.id !== 0)
								.map((queue: QueueResponse) => (
									<SelectItem key={queue.id} value={queue.slug}>
										{queue.icon} {queue.name}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
				);

			case 'archive':
			case 'delete':
				return (
					<span className="flex-1 text-sm text-muted-foreground italic">
						{action.type === 'archive' ? 'Link will be archived' : 'Link will be deleted'}
					</span>
				);

			case 'webhook': {
				const config = (action.value as WebhookConfig) || { url: '', method: 'POST' };
				return (
					<div className="flex-1 space-y-2">
						<div className="flex gap-2">
							<Select
								value={config.method || 'POST'}
								onValueChange={(method) =>
									updateAction(index, { value: { ...config, method: method as 'POST' | 'PUT' } })
								}
							>
								<SelectTrigger className="w-24">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="POST">POST</SelectItem>
									<SelectItem value="PUT">PUT</SelectItem>
								</SelectContent>
							</Select>
							<Input
								value={config.url || ''}
								onChange={(e) => updateAction(index, { value: { ...config, url: e.target.value } })}
								placeholder="https://..."
								className="flex-1"
							/>
						</div>
						<div className="flex items-center gap-2">
							<Checkbox
								id={`webhook-include-${index}`}
								checked={config.include_link_data ?? true}
								onCheckedChange={(checked) =>
									updateAction(index, { value: { ...config, include_link_data: !!checked } })
								}
							/>
							<Label htmlFor={`webhook-include-${index}`} className="text-sm">
								Include link data in request body
							</Label>
						</div>
					</div>
				);
			}

			default:
				return null;
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] p-0 gap-0 overflow-hidden">
				{/* Header */}
				<DialogHeader className="px-6 pt-6 pb-4 border-b">
					<div className="flex items-center gap-2">
						<Button variant="ghost" size="icon" onClick={onClose} className="size-8 shrink-0">
							<ArrowLeft className="size-4" />
						</Button>
						<DialogTitle className="text-xl">
							{isEditing ? 'Edit Rule' : 'New Rule'}
						</DialogTitle>
					</div>
					<DialogDescription>
						{isEditing
							? `Editing "${rule?.name}"`
							: 'Create a rule to automatically process incoming links'}
					</DialogDescription>
				</DialogHeader>

				{/* Content */}
				<ScrollArea className="flex-1 max-h-[calc(90vh-12rem)]">
					<div className="px-6 py-4 space-y-6">
						{/* Name & Description */}
						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="rule-name">Name</Label>
								<Input
									id="rule-name"
									value={formData.name}
									onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
									placeholder="My filter rule"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="rule-description">Description (optional)</Label>
								<Input
									id="rule-description"
									value={formData.description}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, description: e.target.value }))
									}
									placeholder="What does this rule do?"
								/>
							</div>
						</div>

						{/* Conditions */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<Label>Conditions (all must match)</Label>
								<Button variant="ghost" size="sm" onClick={addCondition}>
									<Plus className="size-4 mr-1" />
									Add
								</Button>
							</div>
							<div className="space-y-2">
								{formData.conditions.map((condition, index) => (
									<div key={index} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
										<Select
											value={condition.type}
											onValueChange={(value) =>
												updateCondition(index, {
													type: value as ConditionType,
													value: '',
													field: undefined,
												})
											}
										>
											<SelectTrigger className="w-48">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{CONDITION_TYPES.map((type) => (
													<SelectItem key={type} value={type}>
														{CONDITION_LABELS[type]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>

										{renderConditionValue(condition, index)}

										{formData.conditions.length > 1 && (
											<Button
												variant="ghost"
												size="icon"
												onClick={() => removeCondition(index)}
												className="size-8 shrink-0"
											>
												<Trash2 className="size-4" />
											</Button>
										)}
									</div>
								))}
							</div>
						</div>

						{/* Actions */}
						<div className="space-y-3">
							<div className="flex items-center justify-between">
								<Label>Actions</Label>
								<Button variant="ghost" size="sm" onClick={addAction}>
									<Plus className="size-4 mr-1" />
									Add
								</Button>
							</div>
							<div className="space-y-2">
								{formData.actions.map((action, index) => (
									<div key={index} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
										<Select
											value={action.type}
											onValueChange={(value) =>
												updateAction(index, {
													type: value as ActionType,
													value: value === 'add_tag' || value === 'remove_tag' ? [] : '',
												})
											}
										>
											<SelectTrigger className="w-40">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{ACTION_TYPES.map((type) => (
													<SelectItem key={type} value={type}>
														{ACTION_LABELS[type]}
													</SelectItem>
												))}
											</SelectContent>
										</Select>

										{renderActionValue(action, index)}

										{formData.actions.length > 1 && (
											<Button
												variant="ghost"
												size="icon"
												onClick={() => removeAction(index)}
												className="size-8 shrink-0"
											>
												<Trash2 className="size-4" />
											</Button>
										)}
									</div>
								))}
							</div>
						</div>

						{/* Options */}
						<div className="flex items-center gap-2">
							<Checkbox
								id="stop-processing"
								checked={formData.stopProcessing}
								onCheckedChange={(checked) =>
									setFormData((prev) => ({ ...prev, stopProcessing: !!checked }))
								}
							/>
							<Label htmlFor="stop-processing" className="text-sm">
								Stop processing more rules after this one matches
							</Label>
						</div>

						{/* Test Results */}
						{testResults && (
							<div className="p-4 bg-muted/50 rounded-lg space-y-2">
								<div className="flex items-center justify-between">
									<p className="font-medium">
										Test Results: {testResults.matched}/{testResults.total} matched
									</p>
									<Button variant="ghost" size="sm" onClick={() => setTestResults(null)}>
										<X className="size-4" />
									</Button>
								</div>
								{testResults.results.length > 0 && (
									<div className="space-y-1 max-h-40 overflow-y-auto">
										{testResults.results.map((r) => (
											<div
												key={r.linkId}
												className={cn(
													'text-xs p-2 rounded flex items-center gap-2',
													r.matched ? 'bg-green-500/10' : 'bg-muted'
												)}
											>
												<span className={r.matched ? 'text-green-600' : 'text-muted-foreground'}>
													{r.matched ? '✓' : '✗'}
												</span>
												<span className="truncate">{r.title || r.url}</span>
											</div>
										))}
									</div>
								)}
							</div>
						)}

						{/* Error */}
						{error && <p className="text-sm text-destructive">{error}</p>}
					</div>
				</ScrollArea>

				{/* Footer */}
				<DialogFooter className="px-6 py-4 border-t gap-2">
					<Button
						variant="outline"
						onClick={handleTest}
						disabled={testMutation.isPending}
					>
						<TestTube2 className="size-4 mr-2" />
						{testMutation.isPending ? 'Testing...' : 'Test'}
					</Button>
					<div className="flex-1" />
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Rule'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function getConditionPlaceholder(type: ConditionType): string {
	switch (type) {
		case 'url_domain':
			return 'e.g., reddit.com';
		case 'url_path':
			return 'e.g., ^/r/[^/]+/comments';
		case 'url_pattern':
			return 'e.g., https://.*\\.reddit\\.com/.*';
		case 'title_contains':
			return 'e.g., announcement';
		case 'description_contains':
			return 'e.g., sale';
		default:
			return 'Enter value...';
	}
}
